import { addCalendarDays, parseDateOnlyAsUTC } from "@common/helpers/date";
import type {
    DensityChartDay,
    DensityChartPeriod,
    DensityChartWindow,
} from "./DensityChart.lib";

export const DENSITY_CHART_BUCKET_UNITS = ["day", "week", "month"] as const;

/** What one bar covers: a calendar day, a Monday-to-Sunday week, or a calendar month. */
export type DensityChartBucketUnit =
    (typeof DENSITY_CHART_BUCKET_UNITS)[number];

export interface DensityChartBucket {
    start: string;
    end: string;
    dates: string[];
}

/** Bar 0 of every unit starts here, a Monday and a 1st. */
const EPOCH = "2001-01-01";
const MONDAY = 1;
const WEEK_DAYS = 7;
const MONTHS_PER_YEAR = 12;

export function listDates(startDate: string, endDate: string): string[] {
    const dates: string[] = [];
    for (let date = startDate; date <= endDate;) {
        dates.push(date);
        date = addCalendarDays(date, 1);
    }
    return dates;
}

export const isMonday = (date: string) =>
    parseDateOnlyAsUTC(date).getUTCDay() === MONDAY;

export const isMonthStart = (date: string) => date.slice(8) === "01";

const DAY_MS = 24 * 60 * 60 * 1000;
const EPOCH_YEAR = Number(EPOCH.slice(0, 4));

export const daysBetween = (from: string, to: string) =>
    Math.round(
        (parseDateOnlyAsUTC(to).getTime() -
            parseDateOnlyAsUTC(from).getTime()) /
            DAY_MS,
    );

/** Whole bars from the epoch to the bar holding `date`, negative before it. */
export function toBarIndex({
    date,
    unit,
}: {
    date: string;
    unit: DensityChartBucketUnit;
}): number {
    if (unit === "month") {
        const year = Number(date.slice(0, 4));
        const month = Number(date.slice(5, 7));
        return (year - EPOCH_YEAR) * MONTHS_PER_YEAR + month - 1;
    }
    const days = daysBetween(EPOCH, date);
    return unit === "week" ? Math.floor(days / WEEK_DAYS) : days;
}

/** The first day of bar `index`. */
export function barStartDate({
    index,
    unit,
}: {
    index: number;
    unit: DensityChartBucketUnit;
}): string {
    if (unit === "month") {
        const year = EPOCH_YEAR + Math.floor(index / MONTHS_PER_YEAR);
        const month = index - (year - EPOCH_YEAR) * MONTHS_PER_YEAR + 1;
        return `${year}-${String(month).padStart(2, "0")}-01`;
    }
    return addCalendarDays(EPOCH, unit === "week" ? index * WEEK_DAYS : index);
}

/** Where `date` starts in bars from the epoch: its bar, plus its days' share of that bar before it. */
export function dayPosition({
    date,
    unit,
}: {
    date: string;
    unit: DensityChartBucketUnit;
}): number {
    const index = toBarIndex({ date, unit });
    const barStart = barStartDate({ index, unit });
    return (
        index +
        daysBetween(barStart, date) /
            daysBetween(barStart, barStartDate({ index: index + 1, unit }))
    );
}

const startsBar = ({
    date,
    unit,
}: {
    date: string;
    unit: DensityChartBucketUnit;
}): boolean =>
    unit === "day" || (unit === "week" ? isMonday(date) : isMonthStart(date));

/** The bars over the days, whole days, weeks or months; the first and last keep only days in the range. */
export function buildBuckets({
    startDate,
    endDate,
    unit,
}: {
    startDate: string;
    endDate: string;
    unit: DensityChartBucketUnit;
}): DensityChartBucket[] {
    const buckets: DensityChartBucket[] = [];
    for (const date of listDates(startDate, endDate)) {
        const current = buckets.at(-1);
        if (current && !startsBar({ date, unit })) {
            current.end = date;
            current.dates.push(date);
        } else {
            buckets.push({ start: date, end: date, dates: [date] });
        }
    }
    return buckets;
}

/**
 * How many leading bars end before `historyStart`: the zone before it,
 * every bar when none reaches it.
 */
export function countBarsBefore(
    buckets: DensityChartBucket[],
    historyStart: string | undefined,
): number {
    if (historyStart === undefined) {
        return 0;
    }
    const firstRealBar = buckets.findIndex(({ end }) => end >= historyStart);
    return firstRealBar === -1 ? buckets.length : firstRealBar;
}

export const bucketToWindow = ({
    start,
    end,
}: DensityChartBucket): DensityChartWindow => ({ start, end });

export const bucketToPeriod = ({
    start,
    end,
    dates,
}: DensityChartBucket): DensityChartPeriod => ({
    start,
    end,
    count: dates.length,
});

interface BucketSeriesTotalParams {
    bucket: DensityChartBucket;
    dayByDate: ReadonlyMap<string, DensityChartDay>;
    seriesId: string;
}

export const bucketSeriesTotal = ({
    bucket,
    dayByDate,
    seriesId,
}: BucketSeriesTotalParams): number =>
    bucket.dates.reduce(
        (sum, date) => sum + (dayByDate.get(date)?.values[seriesId] ?? 0),
        0,
    );
